/**
 * HeaderSearch — header search icon → inline dropdown with live results.
 *
 * UX:
 *   • Click the magnifier icon → input expands, dropdown shows featured items.
 *   • Type ≥ 2 chars → debounced search across gemstones, auctions, birthstones, zodiacs.
 *   • Arrow keys + Enter to navigate. Click outside or press Esc to close.
 *   • Each result shows thumbnail + title + subtitle, navigates to the item page.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Search, X } from "lucide-react";
import api from "../lib/api";

const SUPPORTED_LOCALES = ["en", "id", "zh", "th", "ar"];

const KIND_LABEL = {
  gemstone: "Gemstone",
  auction: "Auction",
  birthstone: "Birthstone",
  zodiac: "Zodiac",
};

const HeaderSearch = () => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const inputRef = useRef(null);
  const wrapRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Derive current locale slug from URL (matches Header.jsx convention).
  const currentSlug =
    SUPPORTED_LOCALES.find((l) => location.pathname.startsWith(`/${l}/`) || location.pathname === `/${l}`) || "en";
  const lp = (path) => `/${currentSlug}${path === "/" ? "" : path}`;

  // Outside click closes
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Focus on open
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 40);
    }
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(() => {
      api
        .get(`/search?q=${encodeURIComponent(q)}`)
        .then(({ data }) => {
          if (cancelled) return;
          setResults(data.results || []);
          setHighlight(-1);
        })
        .catch(() => {
          if (!cancelled) setResults([]);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, q.length >= 2 ? 220 : 0);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [q, open]);

  const navigateTo = (url) => {
    setOpen(false);
    setQ("");
    navigate(lp(url));
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (!results.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const target = results[highlight] || results[0];
      if (target) navigateTo(target.url);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (results[0]) navigateTo(results[0].url);
    else if (q.trim()) navigateTo(`/collection?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <div ref={wrapRef} className="relative">
      {!open && (
        <button
          type="button"
          data-testid="header-search-open"
          aria-label="Search"
          onClick={() => setOpen(true)}
          className="text-white/70 hover:text-gold transition-colors"
        >
          <Search size={18} />
        </button>
      )}

      {open && (
        <form
          onSubmit={onSubmit}
          className="flex items-center gap-2 border border-gold/40 bg-black px-3 py-1.5 w-[260px] lg:w-[320px]"
        >
          <Search size={14} className="text-gold flex-shrink-0" />
          <input
            ref={inputRef}
            data-testid="header-search-input"
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search rubies, sapphires, origins…"
            className="bg-transparent text-ivory text-sm flex-1 outline-none placeholder:text-white/30"
          />
          <button
            type="button"
            data-testid="header-search-close"
            onClick={() => { setOpen(false); setQ(""); }}
            aria-label="Close search"
            className="text-white/40 hover:text-white"
          >
            <X size={14} />
          </button>
        </form>
      )}

      {open && (
        <div
          data-testid="header-search-dropdown"
          className="absolute right-0 mt-2 w-[360px] sm:w-[420px] bg-onyx border border-white/10 shadow-2xl max-h-[70vh] overflow-y-auto z-[80]"
        >
          {loading && (
            <div className="px-4 py-6 text-center text-white/40 text-xs font-accent tracking-[0.24em] uppercase">
              Searching…
            </div>
          )}

          {!loading && results.length === 0 && q.length >= 2 && (
            <div className="px-4 py-8 text-center">
              <div className="text-white/50 text-sm">
                No results for <span className="text-gold">"{q}"</span>.
              </div>
              <button
                onClick={() => navigateTo(`/collection?q=${encodeURIComponent(q.trim())}`)}
                className="mt-3 font-accent text-[10px] tracking-[0.24em] uppercase border border-gold text-gold px-4 py-2 hover:bg-gold hover:text-black"
              >
                Search the full collection
              </button>
            </div>
          )}

          {!loading && results.length === 0 && q.length < 2 && (
            <div className="px-4 py-3 text-[10px] tracking-[0.24em] uppercase text-white/40 font-accent">
              Featured
            </div>
          )}

          {!loading && results.length > 0 && (
            <ul>
              {results.map((r, i) => (
                <li key={`${r.kind}-${r.id}`}>
                  <button
                    type="button"
                    data-testid={`header-search-result-${r.id}`}
                    onMouseEnter={() => setHighlight(i)}
                    onClick={() => navigateTo(r.url)}
                    className={`w-full text-left flex items-center gap-3 px-3 py-2.5 transition-colors ${
                      highlight === i ? "bg-gold/10" : "hover:bg-white/5"
                    }`}
                  >
                    <div className="w-12 h-12 bg-black/60 overflow-hidden flex-shrink-0">
                      {r.image && (
                        <img src={r.image} alt="" className="w-full h-full object-cover" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-ivory text-sm truncate">{r.title}</div>
                      <div className="text-white/40 text-xs truncate">
                        <span className="text-gold/80 mr-1">{KIND_LABEL[r.kind] || r.kind}</span>·{" "}
                        {r.subtitle}
                      </div>
                    </div>
                  </button>
                </li>
              ))}
              <li className="border-t border-white/10 px-3 py-2 text-center">
                <button
                  type="button"
                  onClick={() => navigateTo(`/collection${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ""}`)}
                  className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold hover:text-ivory"
                >
                  View all results →
                </button>
              </li>
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default HeaderSearch;
